import React, { FC, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import {
  faArrowDownShortWide,
  faArrowUpShortWide,
  faUsers,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import { useIntl } from "react-intl";

import { FileTagsVisibility } from "@mapmycustomers/shared";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import Visibility from "@mapmycustomers/shared/enum/Visibility";
import FileTag from "@mapmycustomers/shared/types/entity/FileTag";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { Button, Popconfirm, VisibilitySelector } from "@mapmycustomers/ui";

import CreatorFilter from "@app/component/CreatorFilter/CreatorFilter";
import { getCurrentUser } from "@app/store/iam";
import { isManager, isMember, isOwner } from "@app/store/iam/util";
import { getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

import { getTotalFileTags, getViewState } from "../../store";
import { bulkDeleteFileTags, fetchFileTags } from "../../store/actions";
import { DEFAULT_PAGE_SIZE } from "../../store/saga";
import { ALL_VISIBILITIES } from "../../util/const";
import SortField, { getSortFieldDisplayName } from "../../util/SortField";

import getSortDirectionNameForField from "./getSortDirectionNameForField";
import messages from "./messages";
import styles from "./TagList.module.scss";

type ToolbarProps = {
  fileTags: FileTag[];
  onChange: (fileTags: FileTag[]) => void;
  onChangeSelectedTagIds: (ids: FileTag["id"][]) => void;
  selectedTagIds: FileTag["id"][];
} & typeof mapDispatchToProps &
  ReturnType<typeof mapStateToProps>;

const Toolbar: FC<ToolbarProps> = ({
  currentUser,
  fileTags,
  onBulkDeleteFileTags,
  onChange,
  onChangeSelectedTagIds,
  onFetchFileTags,
  selectedTagIds,
  teams,
  total,
  viewState,
}) => {
  const intl = useIntl();

  const { page, sortFieldName, sortOrder, userIds } = viewState;
  const offset = (page - 1) * DEFAULT_PAGE_SIZE;

  const handleSelectDeselectAll = useCallback(() => {
    if (selectedTagIds.length > 0) {
      onChangeSelectedTagIds([]);
    } else {
      onChangeSelectedTagIds(
        fileTags
          .filter(({ userId }) => userId === currentUser.id || isOwner(currentUser))
          .map(({ id }) => id)
      );
    }
  }, [selectedTagIds.length, onChangeSelectedTagIds, fileTags, currentUser]);

  const handleBulkVisibilityChange = useCallback(
    (visibility: FileTagsVisibility, teamId?: Team["id"][]) => {
      onChange(
        fileTags.map((tag) =>
          selectedTagIds.includes(tag.id) ? { ...tag, teamId: teamId ?? [], visibility } : tag
        )
      );
      onChangeSelectedTagIds([]);
    },
    [onChange, onChangeSelectedTagIds, selectedTagIds, fileTags]
  );

  const handleBulkDelete = useCallback(() => {
    onBulkDeleteFileTags({
      callback: () => {
        onChangeSelectedTagIds([]);
      },
      ids: selectedTagIds,
    });
  }, [onBulkDeleteFileTags, onChangeSelectedTagIds, selectedTagIds]);

  const handleSort = useCallback(
    (field, order) => {
      onChangeSelectedTagIds([]);
      onFetchFileTags({ ...viewState, sortFieldName: field, sortOrder: order });
    },
    [onChangeSelectedTagIds, onFetchFileTags, viewState]
  );

  const items: MenuProps["items"] = useMemo(
    () =>
      [SortOrder.ASC, SortOrder.DESC].flatMap((order) =>
        [SortField.NAME, SortField.UPDATED_AT].map((field) => ({
          key: `${field}_${order}`,
          label: getSortDirectionNameForField(intl, field, order),
          onClick: () => handleSort(field, order),
        }))
      ),
    [handleSort, intl]
  );

  const allowedVisibilities = useMemo(
    () =>
      isOwner(currentUser)
        ? ALL_VISIBILITIES
        : isManager(currentUser)
        ? [Visibility.PRIVATE, Visibility.SHARED_WITH_TEAM]
        : [Visibility.PRIVATE],
    [currentUser]
  );

  const handleChangeCreators = useCallback(
    (userIds: User["id"][]) => {
      onChangeSelectedTagIds([]);
      onFetchFileTags({ ...viewState, userIds });
    },
    [onChangeSelectedTagIds, onFetchFileTags, viewState]
  );

  const getCustomLabel = useCallback(
    (visibility: Visibility, defaultLabel: string) => {
      return visibility === Visibility.PRIVATE
        ? intl.formatMessage(messages.private)
        : defaultLabel;
    },
    [intl]
  );

  return (
    <div className={styles.toolbar}>
      <div>
        <span>
          {intl.formatMessage(messages.tagsCount, {
            endRange: offset + fileTags.length,
            startRange: offset + 1,
            total,
          })}
        </span>
        <Button disabled={!fileTags.length} onClick={handleSelectDeselectAll} type="link">
          {intl.formatMessage(selectedTagIds.length ? messages.deselectAll : messages.selectAll)}
        </Button>
        {selectedTagIds.length > 0 && (
          <span>
            {intl.formatMessage(messages.selectedCount, { count: selectedTagIds.length })}
          </span>
        )}
      </div>
      <div className={styles.rightButtons}>
        {selectedTagIds.length > 0 ? (
          <>
            {!isMember(currentUser) && (
              <VisibilitySelector
                allowedVisibilities={allowedVisibilities}
                getLabel={getCustomLabel}
                onChange={handleBulkVisibilityChange}
                subLabel={intl.formatMessage(messages.shareWith)}
                teams={teams}
                userRole={currentUser.role.key}
              >
                <Button leftIcon={<FontAwesomeIcon icon={faUsers} />} type="filled">
                  {intl.formatMessage(messages.sharing)}
                </Button>
              </VisibilitySelector>
            )}
            <Popconfirm
              description={intl.formatMessage(messages.deleteConfirm, {
                count: selectedTagIds.length,
              })}
              onConfirm={handleBulkDelete}
              overlayClassName={styles.popconfirm}
            >
              <Button danger>{intl.formatMessage(messages.delete)}</Button>
            </Popconfirm>
          </>
        ) : (
          <>
            <CreatorFilter onChange={handleChangeCreators} selectedUserIds={userIds} />
            <Dropdown
              menu={{ items, selectedKeys: [`${sortFieldName}_${sortOrder}`] }}
              trigger={["click"]}
            >
              <Button
                leftIcon={
                  <FontAwesomeIcon
                    icon={sortOrder === SortOrder.DESC ? faArrowUpShortWide : faArrowDownShortWide}
                  />
                }
              >
                {getSortFieldDisplayName(intl, sortFieldName)}
              </Button>
            </Dropdown>
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUser: getCurrentUser(state)!,
  teams: getTeams(state),
  total: getTotalFileTags(state),
  viewState: getViewState(state),
});

const mapDispatchToProps = {
  onBulkDeleteFileTags: bulkDeleteFileTags.request,
  onFetchFileTags: fetchFileTags.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Toolbar);
